<template>
  <aside
    :class="[
      'fixed left-0 top-0 transition-all duration-300 ease-in-out h-screen',
      'bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800',
      'shadow-sm',
      isCollapsed ? 'w-20' : 'w-72',
      isMobileView ? 'rounded-r-2xl shadow-2xl w-72 h-screen' : '',
      $attrs.class
    ]"
    style="z-index: 51"
  >
    <nav class="flex flex-col h-full">
      <!-- Header avec hamburger et titre -->
      <div class="h-16 border-b border-gray-200 dark:border-gray-800 flex items-center px-4 flex-shrink-0">
        <button 
          v-if="!isMobileView" 
          @click="$emit('update:modelValue', !isCollapsed)" 
          class="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 p-2 flex items-center justify-center transition-colors flex-shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div 
          class="ml-2 overflow-hidden transition-all duration-300 ease-in-out"
          :class="isCollapsed && !isMobileView ? 'w-0 opacity-0 ml-0' : 'w-auto opacity-100'"
        >
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">K&C Portfolio Manager</h2>
        </div>
      </div>
      <!-- Menu Items -->
      <div class="flex-1 overflow-y-auto overflow-x-hidden pt-3 pb-4">
        <div class="flex flex-col gap-1 px-2 pb-4 pt-0">
          <template v-for="item in filteredMenuItems" :key="item.path">
            <!-- Menu Item avec Tooltip -->
            <Tooltip
              v-if="isCollapsed && !isMobileView"
              :content="item.name"
              placement="right"
              :width="'auto'"
              :open-delay="300"
            >
              <router-link
                :to="item.path"
                :class="[
                  'group relative flex items-center justify-center',
                  'rounded-lg transition-all duration-200',
                  'hover:bg-gray-100 dark:hover:bg-gray-800',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                  isActive(item.path)
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300'
                ]"
                :aria-label="item.name"
              >
                <span
                  class="flex items-center justify-center w-12 h-12"
                  v-html="sanitizeHtml(item.icon)"
                ></span>
                <!-- Indicateur actif -->
                <span
                  v-if="isActive(item.path)"
                  class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-600 dark:bg-blue-400 rounded-r-full"
                ></span>
              </router-link>
            </Tooltip>
            <!-- Menu Item sans Tooltip (sidebar étendue) -->
            <router-link
              v-else
              :to="item.path"
              :class="[
                'group relative flex flex-nowrap items-center overflow-hidden',
                'rounded-lg transition-all duration-200',
                'flex-shrink-0',
                isCollapsed && !isMobileView ? 'px-2 py-2.5 justify-center min-w-[3rem]' : 'px-3 py-2.5',
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
                isActive(item.path)
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300'
              ]"
            >
              <span
                class="flex-shrink-0 w-5 h-5"
                v-html="sanitizeHtml(item.icon)"
              ></span>
              <span
                class="text-lg whitespace-nowrap transition-all duration-300 ease-in-out"
                :class="isCollapsed && !isMobileView ? 'w-0 opacity-0 ml-0 overflow-hidden' : 'w-auto opacity-100 ml-3'"
              >
                {{ item.name }}
              </span>
              <!-- Indicateur actif -->
              <span
                v-if="isActive(item.path)"
                class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-600 dark:bg-blue-400 rounded-r-full"
              ></span>
            </router-link>
          </template>
        </div>
      </div>
      <!-- Footer avec version -->
      <div 
        class="mt-auto border-t border-gray-200 dark:border-gray-800 py-2 flex-shrink-0 relative"
      >
        <!-- Gradient pour atténuer l'espace vide -->
        <div 
          class="absolute inset-x-0 -top-8 h-8 pointer-events-none"
          :class="[
            'bg-gradient-to-b from-transparent',
            isDarkMode ? 'to-gray-900' : 'to-white'
          ]"
        ></div>
        <div class="relative z-10 overflow-hidden transition-all duration-300 ease-in-out">
          <div class="flex items-center justify-center transition-all duration-300 ease-in-out"
            :class="isCollapsed && !isMobileView ? 'px-2' : 'px-4'">
            <span class="text-sm text-gray-600 dark:text-gray-400 inline-block py-1 whitespace-nowrap transition-all duration-300 ease-in-out">
              <span v-if="isCollapsed && !isMobileView" class="inline-block">v{{ displayVersion }}</span>
              <span v-else class="inline-block">Version {{ displayVersion }}</span>
            </span>
          </div>
        </div>
      </div>
    </nav>
  </aside>
</template>
<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/themeStore'
import { storeToRefs } from 'pinia'
import Tooltip from '@/components/common/Tooltip.vue'
import { menuItems as allMenuItems } from '@/config/menuItems'
import { APP_VERSION } from '@/config/constants'
import { sanitizeHtml } from '@/utils/sanitizeHtml'
// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  }
})
// Emits
defineEmits(['update:modelValue'])
// Composables
const route = useRoute()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const { isDarkMode } = storeToRefs(themeStore)
// State
const isMobileView = ref(false)
// Computed
const isCollapsed = computed(() => props.modelValue)
const isAdmin = computed(() => {
  return authStore.user?.role_id === 1
})
const filteredMenuItems = computed(() => {
  return allMenuItems.filter(item => !item.requiresAdmin || isAdmin.value)
})
// Methods
const checkMobileView = () => {
  // Breakpoint mobile standard (1024px = lg)
  isMobileView.value = window.innerWidth < 1024
}
const isActive = (path) => {
  if (path === '/app') {
    return route.path === '/app'
  }
  return route.path.startsWith(path)
}
// Version de l'application
const displayVersion = computed(() => {
  return APP_VERSION.replace(/^v/i, '')
})
// Lifecycle
onMounted(() => {
  checkMobileView()
  window.addEventListener('resize', checkMobileView)
})
onUnmounted(() => {
  window.removeEventListener('resize', checkMobileView)
})
</script>
